import { useEffect, useRef, useState } from "react";

/**
 * AnimatedCounter — counts from 0 → `value` over `duration` ms using
 * requestAnimationFrame easing. Triggers when the element enters the viewport
 * (IntersectionObserver), once per mount.
 *
 * Props:
 *  - value: target numeric value (number)
 *  - duration: total animation duration in ms (default 1800)
 *  - prefix / suffix: optional strings around the number (e.g. "+", "%", "×")
 *  - format: optional (n)=>string for custom formatting; defaults to Math.round
 *  - className: style passthrough
 */
export default function AnimatedCounter({
  value,
  duration = 1800,
  prefix = "",
  suffix = "",
  format,
  className = "",
  "data-testid": testId,
}) {
  const ref = useRef(null);
  const [n, setN] = useState(0);
  const started = useRef(false);

  useEffect(() => {
    if (!ref.current) return;
    const node = ref.current;

    const start = () => {
      if (started.current) return;
      started.current = true;
      const startTs = performance.now();
      const tick = (now) => {
        const t = Math.min(1, (now - startTs) / duration);
        // easeOutCubic
        const eased = 1 - Math.pow(1 - t, 3);
        setN(value * eased);
        if (t < 1) requestAnimationFrame(tick);
        else setN(value);
      };
      requestAnimationFrame(tick);
    };

    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            start();
            io.unobserve(node);
          }
        });
      },
      { threshold: 0.4 }
    );
    io.observe(node);

    return () => io.disconnect();
  }, [value, duration]);

  const display = format ? format(n) : Math.round(n).toLocaleString();

  return (
    <span ref={ref} className={className} data-testid={testId}>
      {prefix}
      {display}
      {suffix}
    </span>
  );
}
